import React from 'react'
import { coreResource } from '@jarvis.oddle/core/env'
import GoogleMapApi from '@jarvis.oddle/core/api/map/gmap'
import { init } from '@jarvis.oddle/core/tools/lib'
import Image from '@jarvis.oddle/core/components/Image'
import MapResource from '@jarvis.oddle/core/components/MapResource'
import GoogleMap from '@jarvis.oddle/core/components/GoogleMap'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { useRouter } from 'next/router'
import { getLanguageCode, parseLocale } from 'src/utils/localisation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'

type GoogleWindow = Window & {
  google?: {
    maps?: {
      places?: any;
      Map?: any;
      Marker?: any;
      LatLngBounds?: any;
    };
  };
}

export const flow = {
  initial: 'start',
  states: {
    start: {
      on: {
        SHOW_DROPDOWN: 'dropdown',
        SELECT_ADDRESS: 'map'
      }
    },
    dropdown: {
      on: {
        CLOSE_DROPDOWN: 'start',
        SELECT_ADDRESS: 'map'
      }
    },
    map: {
      on: {
        NEXT: 'addressTwo',
        RESET: 'start',
        SHOW_DROPDOWN: 'dropdown',
        QUERY_CLEAR: 'start'
      }
    },
    addressTwo: {
      on: {
        QUERY_CLEAR: 'start',
        RESET: 'start',
        SHOW_DROPDOWN: 'dropdown'
      }
    }
  }
}

const GoogleMapService = ({ children }: { children? }): JSX.Element => {
  const router = useRouter()
  const { t } = React.useContext(TranslationContext)
  const { language, country } = parseLocale(router.query.locale as string)
  const {
    googleMapId: apiKey,
    menuCountry,
    countryCode,
    autocompleteFields,
    // checkoutAddressFormat
  } = useAppSelector(selectDetailMenuSite) || {}
  const [mapResources, setMapResources] = React.useState({})

  const getLocalisationParams = () => ({
    country,
    language: getLanguageCode(language, country)
  })

  const getFieldMapper = (country) => {
    // if (country === 'Taiwan' && checkoutAddressFormat?.autocompleteAddressFields) {
    //   return checkoutAddressFormat.autocompleteAddressFields.reduce((obj, field) => ({
    //     ...obj,
    //     [field[0]]: field.slice(1)
    //   }), {})
    // }
    // if (country === 'Taiwan') {
    //   return {
    //     addressOne: ['route', 'street_number', 'premise'],
    //     city: ['administrative_area_level_3', 'administrative_area_level_4'],
    //     postal: ['postal_code'],
    //     state: ['administrative_area_level_1', 'administrative_area_level_2']
    //   }
    // }
    if (country === 'Taiwan') {
      return {
        addressOne: ['route', 'street_number', 'premise'],
        city: ['administrative_area_level_1', 'administrative_area_level_2'],
        postal: ['postal_code'],
        state: ['']
      }
    }
    return autocompleteFields
      ?.substr(1, autocompleteFields.length - 2)
      ?.match(/\[[\w, ]+]/g)
      ?.map((s) => s.match(/\w+/g))
      ?.reduce((o, g) => ({ ...o, [g[0]]: [...g.slice(1)] }), {})
  }

  const getMapResources = () => {
    const { language } = getLocalisationParams()
    const mapServices = window.google.maps
    const mapApi = GoogleMapApi({
      apiKey,
      mapServices,
      countryCode,
      country: menuCountry,
      language,
      mapper: getFieldMapper(menuCountry)
    })

    return {
      mapServices,
      MapComponent: GoogleMap,
      MapServiceLogo: (
        <Image
          src={coreResource('/images/powered_by_google.png')}
          alt={t('Powered by Google') as string}
        />
      ),
      ...mapApi
    }
  }

  React.useEffect(() => {
    const { language } = getLocalisationParams()

    async function initMap() {
      await init(
        `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=${language}&loading=async`,
        `google-maps-js-${language}`
      )
      
      // Wait for the Google Maps API to be fully initialized
      await new Promise<void>((resolve) => {
        const googleWindow = window as GoogleWindow
        if (googleWindow.google?.maps?.places) {
          resolve()
        } else {
          const checkInterval = setInterval(() => {
            if (googleWindow.google?.maps?.places) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        }
      })

      setMapResources(getMapResources())
    }

    initMap().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MapResource {...mapResources}>
      {(mapResourceProps) => children({ flow, ...mapResourceProps })}
    </MapResource>
  )
}

export default GoogleMapService
